import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, Modal, Text } from '@fragments';
import { IconAlertCircle, IconPlayerPauseFilled, IconPlayerPlayFilled, IconRefresh, IconVolume, IconVolumeOff } from '@tabler/icons-react';
import { paintFilmstrip } from '../../../render/video';
import { VIDEO_LIMITS } from '../../../model/schema';
import { fitSelection } from '../../../model/timeline';
import { clockLabel, secondsLabel } from '../Timeline';
import './styles.scss';

const FRAMES = 12;
const STEP = 0.1;

// The file's frames across the trimmer, each painted as it arrives.
function Strip({ url, duration }) {
  const canvas = useRef(null);
  useEffect(() => (canvas.current && url && duration ? paintFilmstrip(canvas.current, url, { from: 0, to: duration, count: FRAMES, maxSide: 200 }) : undefined), [url, duration]);
  return <canvas ref={canvas} className="art-trim__strip" aria-hidden="true" />;
}

// The trim dialog (2.13.0): the part of a video a carousel page plays. It opens when a video
// is sent (while it uploads), picked from the creator's videos, or from a clip's "Cortar".
// The preview plays only the selection; on the trimmer under it the window drags as a whole
// and by its edges (arrows ±0.1 s, Shift ±1 s). A page plays up to 60 s, so the selection
// takes at most what the page has left: `limits.background` after the background's clips
// (a new background clip joins them) or `limits.element` (a clip over the page starts at 0).
// `upload` is `{ status: 'uploading' | 'done' | 'error', progress }` while a sent file goes up;
// the clip is added once it is stored. `onConfirm({ trim, background, muted })`.
export default function VideoTrimDialog({ open, video, mode = 'add', initial = null, background: startsAsBackground = false, limits, muted: startsMuted = false,
  upload = null, onRetryUpload, onConfirm, onClose }) {
  const { t, i18n } = useTranslation('artEditor');
  const player = useRef(null);
  const strip = useRef(null);
  const drag = useRef(null);
  const [background, setBackground] = useState(startsAsBackground);
  const [muted, setMuted] = useState(startsMuted);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const duration = video?.duration || 0;
  const roomFor = asBackground => Math.max(0, (asBackground ? limits?.background : limits?.element) ?? VIDEO_LIMITS.pageSeconds);
  const shortest = Math.min(VIDEO_LIMITS.minClipSeconds, duration);
  const backgroundFits = mode !== 'add' || roomFor(true) >= shortest;
  const max = roomFor(mode === 'add' && background);
  const [selection, setSelection] = useState(() => fitSelection(initial || { start: 0, end: duration }, { duration, max, moved: 'end' }));

  useEffect(() => {
    if (!open) return;
    const asBackground = mode === 'add' && startsAsBackground && roomFor(true) >= shortest;
    setBackground(asBackground);
    setMuted(startsMuted);
    setSelection(fitSelection(initial || { start: 0, end: duration }, { duration, max: roomFor(mode === 'add' && asBackground), moved: 'end' }));
    setPlaying(false);
  }, [open, video?.url]); // eslint-disable-line react-hooks/exhaustive-deps
  // A smaller room (the background's) cuts the selection to fit.
  useEffect(() => { setSelection(value => fitSelection(value, { duration, max, moved: 'end' })); }, [max, duration]);

  // The preview plays the selection, then stops at its start again.
  useLayoutEffect(() => {
    const element = player.current;
    if (!element || !playing) return undefined;
    let frame = 0;
    const loop = () => {
      setCurrent(element.currentTime);
      if (element.currentTime >= selection.end - 0.03) { element.pause(); element.currentTime = selection.start; setPlaying(false); return; }
      frame = requestAnimationFrame(loop);
    };
    if (element.currentTime < selection.start - 0.05 || element.currentTime >= selection.end - 0.05) element.currentTime = selection.start;
    element.play().then(() => { frame = requestAnimationFrame(loop); }).catch(() => setPlaying(false));
    return () => { cancelAnimationFrame(frame); element.pause(); };
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps
  const show = time => {
    const element = player.current;
    if (!element) return;
    element.currentTime = Math.max(0, Math.min(duration, time));
    setCurrent(element.currentTime);
  };

  const fraction = time => (duration ? `${(time / duration) * 100}%` : '0%');
  const change = (next, moved) => {
    const fitted = fitSelection(next, { duration, max, moved });
    setSelection(fitted);
    show(moved === 'end' ? fitted.end : fitted.start);
  };
  const onDown = (event, moved) => {
    if (event.button !== 0 || !duration) return;
    event.preventDefault();
    event.stopPropagation();
    setPlaying(false);
    drag.current = { moved, x: event.clientX, from: selection, width: strip.current.getBoundingClientRect().width };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onMove = event => {
    const current = drag.current;
    if (!current) return;
    const delta = ((event.clientX - current.x) / current.width) * duration;
    const { start, end } = current.from;
    if (current.moved === 'both') change({ start: start + delta, end: end + delta }, 'both');
    else if (current.moved === 'start') change({ start: start + delta, end }, 'start');
    else change({ start, end: end + delta }, 'end');
  };
  const onUp = event => { drag.current = null; event.currentTarget.releasePointerCapture?.(event.pointerId); };
  const nudge = (event, moved) => {
    const step = (event.shiftKey ? 10 : 1) * STEP * (event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? -1 : 1);
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    change(moved === 'start' ? { ...selection, start: selection.start + step } : { ...selection, end: selection.end + step }, moved);
  };
  const handlers = { onPointerMove: onMove, onPointerUp: onUp, onPointerCancel: onUp };

  const length = selection.end - selection.start;
  const uploading = upload?.status === 'uploading';
  const failed = upload?.status === 'error';
  const ratio = video ? `${video.width} / ${video.height}` : '16 / 9';
  const confirm = () => onConfirm({ trim: selection, background: mode === 'add' && background, muted });
  return <Modal open={open} onClose={onClose} size="lg" className="art-trim-modal" closeLabel={t('close')}
    title={t(mode === 'add' ? 'trimTitleAdd' : 'trimTitleEdit')} subtitle={t('trimSubtitle', { seconds: VIDEO_LIMITS.pageSeconds })}
    footer={<>
      <Button variant="rounded" color="overlay" preventLoading onClick={onClose}>{t('cancel')}</Button>
      {/* Keyed: the Button fragment reads `loading` only when it mounts. */}
      <Button key={uploading ? 'uploading' : 'ready'} variant="rounded" color="inverse" preventLoading loading={uploading} disabled={uploading || failed || !duration || length < shortest - 1e-3} onClick={confirm}>
        {t(mode === 'add' ? 'trimAdd' : 'trimApply')}</Button>
    </>}>
    <div className="art-trim">
      <div className="art-trim__preview" style={{ aspectRatio: ratio }}>
        {video?.url && <video ref={player} src={video.url} muted={muted} playsInline preload="auto" onLoadedMetadata={() => show(selection.start)} />}
        <Button className="art-trim__play" size="medium" variant="rounded" color="inverse" preventLoading icon={playing ? <IconPlayerPauseFilled /> : <IconPlayerPlayFilled />}
          aria-label={t(playing ? 'pause' : 'playSelection')} tooltip={t(playing ? 'pause' : 'playSelection')} disabled={!duration} onClick={() => setPlaying(value => !value)} />
      </div>

      {/* The upload goes on while the creator trims: it shows right under the preview. */}
      {upload && upload.status !== 'done' && <div className={`art-trim__upload${failed ? ' is-error' : ''}`} role={failed ? 'alert' : 'status'}>
        {failed ? <>
          <Text.caption contrast={8} left={<IconAlertCircle />}>{t('videoUploadFailed')}</Text.caption>
          {onRetryUpload && <Button size="small" variant="rounded" color="overlay" preventLoading startIcon={<IconRefresh />} onClick={onRetryUpload}>{t('retry')}</Button>}
        </> : <>
          <Text.caption contrast={7}>{t('videoUploading', { percent: Math.round((upload.progress || 0) * 100) })}</Text.caption>
          <span className="art-trim__bar" aria-hidden="true"><span style={{ transform: `scaleX(${Math.max(0.02, upload.progress || 0)})` }} /></span>
        </>}
      </div>}

      <div className="art-trim__trimmer">
        <div ref={strip} className="art-trim__track" onPointerDown={event => {
          if (event.button !== 0 || !duration || event.target !== event.currentTarget) return;
          const rect = event.currentTarget.getBoundingClientRect();
          show(((event.clientX - rect.left) / rect.width) * duration);
        }}>
          <Strip url={video?.url} duration={duration} />
          <span className="art-trim__shade is-before" style={{ width: fraction(selection.start) }} />
          <span className="art-trim__shade is-after" style={{ left: fraction(selection.end) }} />
          <div className="art-trim__window" style={{ left: fraction(selection.start), width: fraction(length) }} onPointerDown={event => onDown(event, 'both')} {...handlers}>
            <span className="art-trim__grip is-start" role="slider" tabIndex={0} aria-label={t('trimStart')} aria-valuemin={0} aria-valuemax={Math.round(duration * 10) / 10}
              aria-valuenow={Math.round(selection.start * 10) / 10} aria-valuetext={clockLabel(selection.start, i18n.language)}
              onPointerDown={event => onDown(event, 'start')} onKeyDown={event => nudge(event, 'start')} />
            <span className="art-trim__grip is-end" role="slider" tabIndex={0} aria-label={t('trimEnd')} aria-valuemin={0} aria-valuemax={Math.round(duration * 10) / 10}
              aria-valuenow={Math.round(selection.end * 10) / 10} aria-valuetext={clockLabel(selection.end, i18n.language)}
              onPointerDown={event => onDown(event, 'end')} onKeyDown={event => nudge(event, 'end')} />
          </div>
          <span className="art-trim__playhead" style={{ left: fraction(current) }} aria-hidden="true" />
        </div>
        <div className="art-trim__times">
          <Text.caption contrast={6}>{clockLabel(selection.start, i18n.language)}</Text.caption>
          <Text.label contrast={9}>{t('trimLength', { length: secondsLabel(length, i18n.language), max: secondsLabel(Math.min(max, duration), i18n.language) })}</Text.label>
          <Text.caption contrast={6}>{clockLabel(selection.end, i18n.language)}</Text.caption>
        </div>
      </div>

      <div className="art-trim__options">
        {mode === 'add' && <div className="art-trim__field">
          <Text.label contrast={9}>{t('videoPlacement')}</Text.label>
          <ButtonGroup size="small" value={background ? 'background' : 'element'} onChange={value => { if (value !== 'background' || backgroundFits) setBackground(value === 'background'); }}
            options={[{ value: 'background', label: t('videoAsBackground') }, { value: 'element', label: t('videoAsElement') }]} />
          <Text.caption contrast={5}>{!backgroundFits ? t('videoBackgroundFull', { seconds: VIDEO_LIMITS.pageSeconds })
            : background ? t('videoAsBackgroundHint', { from: clockLabel(VIDEO_LIMITS.pageSeconds - roomFor(true), i18n.language) }) : t('videoAsElementHint')}</Text.caption>
        </div>}
        <div className="art-trim__field">
          <Text.label contrast={9}>{t('videoSound')}</Text.label>
          <Button size="small" variant="rounded" color="overlay" preventLoading startIcon={muted ? <IconVolumeOff /> : <IconVolume />} aria-pressed={!muted} onClick={() => setMuted(value => !value)}>
            {t(muted ? 'videoSoundOff' : 'videoSoundOn')}</Button>
        </div>
      </div>

    </div>
  </Modal>;
}
